import type { ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from './Button';

export interface FormToggleButtonProps {
  /** Whether the form this button controls is currently showing. */
  isOpen: boolean;
  /** What it says while the form is closed — "Assign new activity". */
  label: string;
  onToggle: () => void;
  /** The icon while closed. Closing always shows a cross. */
  icon?: ReactNode;
  /** Names the form for a screen reader, which cannot see it appear. */
  controls?: string;

  className?: string;
}

/**
 * The button that opens a form on the page, and closes it again.
 *
 * One control doing two jobs, so it has to look like whichever one it is
 * doing — which is the whole reason this exists as a component rather than as
 * a `<Button>` with a ternary label in each screen that wanted one:
 *
 *   closed   the page's main action, in the accent, with a `+`.
 *   open     a retreat. Quiet, and marked with a cross.
 *
 * Drawn the other way round — a solid accent button reading "Cancel", carrying
 * a `+` — it becomes the loudest thing on screen, sits next to the Save it
 * would throw away, and shows an icon that says the opposite of its own label.
 *
 * `aria-expanded` is set for the same reason the icon changes: somebody who
 * cannot see the form appear still needs to know the button toggles it.
 */
export function FormToggleButton({
  isOpen,
  label,
  onToggle,
  icon,
  controls,
  className,
}: FormToggleButtonProps) {
  return (
    <Button
      variant={isOpen ? 'secondary' : 'primary'}
      aria-expanded={isOpen}
      aria-controls={controls}
      leftIcon={isOpen ? <X className="h-4 w-4" /> : (icon ?? <Plus className="h-4 w-4" />)}
      onClick={onToggle}
      className={className}
    >
      {isOpen ? 'Cancel' : label}
    </Button>
  );
}
